import { supabase, TABLES, getETHTokens, getUSD1Tokens, getSolanaTokens, ETHTokenData, USD1TokenData, SolanaTokenData } from './supabase';
import type { TokenData } from '../types';

export type ChainFilter = 'ink' | 'eth' | 'wlfi' | 'mon' | 'sol';

export interface UnifiedToken {
  id?: string;
  name: string;
  symbol: string;
  description?: string;
  website?: string;
  twitter?: string;
  telegram?: string;
  discord?: string;
  logo_url?: string;
  cover_photo_url?: string;
  contract_address: string;
  network: string;
  pool_address?: string;
  nft_token_id?: string;
  deployer_address: string;
  initial_buy_amount?: string;
  created_at?: string;
  updated_at?: string;
  favorites_count?: number;
  creator_username?: string;
  category?: string;
  dex?: string;
  base_token?: string;
  tx_hash?: string;
  chain: ChainFilter;
}

/**
 * Fetch tokens from a specific chain
 */
export async function fetchTokensByChain(
  chain: ChainFilter,
  options?: {
    limit?: number;
    orderBy?: 'created_at' | 'favorites_count';
    ascending?: boolean;
    searchQuery?: string;
  }
): Promise<UnifiedToken[]> {
  const searchQuery = options?.searchQuery?.toLowerCase().trim();
  const limit = options?.limit || 100;
  const orderBy = options?.orderBy || 'created_at';
  const ascending = options?.ascending ?? false;

  try {
    switch (chain) {
      case 'ink': {
        let query = supabase
          .from(TABLES.TOKENS)
          .select('*')
          .eq('network', 'ink');

        if (searchQuery) {
          query = query.or(
            `name.ilike.%${searchQuery}%,symbol.ilike.%${searchQuery}%,contract_address.ilike.%${searchQuery}%`
          );
        }

        query = query.order(orderBy, { ascending }).limit(limit);

        const { data, error } = await query;
        if (error) throw error;

        return (data || []).map((token: TokenData) => ({
          ...token,
          network: token.network || 'ink',
          chain: 'ink' as ChainFilter,
        }));
      }

      case 'eth': {
        const tokens = await getETHTokens({
          limit,
          orderBy: orderBy === 'favorites_count' ? 'created_at' : orderBy, // ETH table might not have favorites_count
          ascending,
        });

        let filtered = tokens;
        if (searchQuery) {
          filtered = tokens.filter(
            (t) =>
              t.name.toLowerCase().includes(searchQuery) ||
              t.symbol.toLowerCase().includes(searchQuery) ||
              t.contract_address.toLowerCase().includes(searchQuery)
          );
        }

        return filtered.map((token: ETHTokenData) => ({
          ...token,
          network: token.network || 'ethereum',
          chain: 'eth' as ChainFilter,
        }));
      }

      case 'wlfi': {
        const tokens = await getUSD1Tokens({
          limit,
          orderBy: orderBy === 'favorites_count' ? 'created_at' : orderBy,
          ascending,
        });

        let filtered = tokens;
        if (searchQuery) {
          filtered = tokens.filter(
            (t) =>
              t.name.toLowerCase().includes(searchQuery) ||
              t.symbol.toLowerCase().includes(searchQuery) ||
              t.contract_address.toLowerCase().includes(searchQuery)
          );
        }

        return filtered.map((token: USD1TokenData) => ({
          ...token,
          network: token.network || 'ethereum',
          chain: 'wlfi' as ChainFilter,
        }));
      }

      case 'mon': {
        let query = supabase
          .from('tokens_monad')
          .select('*');

        if (searchQuery) {
          query = query.or(
            `name.ilike.%${searchQuery}%,symbol.ilike.%${searchQuery}%,contract_address.ilike.%${searchQuery}%`
          );
        }

        query = query.order(orderBy === 'favorites_count' ? 'created_at' : orderBy, { ascending }).limit(limit);

        const { data, error } = await query;
        if (error) throw error;

        return (data || []).map((token: any) => ({
          ...token,
          chain: 'mon' as ChainFilter,
        }));
      }

      case 'sol': {
        const tokens = await getSolanaTokens({
          limit,
          orderBy: orderBy === 'favorites_count' ? 'created_at' : orderBy,
          ascending,
        });

        let filtered = tokens;
        if (searchQuery) {
          filtered = tokens.filter(
            (t) =>
              t.name.toLowerCase().includes(searchQuery) ||
              t.symbol.toLowerCase().includes(searchQuery) ||
              t.mint_address.toLowerCase().includes(searchQuery) ||
              t.contract_address.toLowerCase().includes(searchQuery)
          );
        }

        return filtered.map((token: SolanaTokenData) => ({
          ...token,
          contract_address: token.mint_address, // Use mint_address as contract_address for compatibility
          network: 'solana',
          chain: 'sol' as ChainFilter,
        }));
      }

      default:
        return [];
    }
  } catch (error) {
    console.error(`[UnifiedTokenFetcher] Error fetching ${chain} tokens:`, error);
    return [];
  }
}

/**
 * Fetch featured token for a specific chain
 */
export async function fetchFeaturedTokenByChain(chain: ChainFilter): Promise<UnifiedToken | null> {
  try {
    switch (chain) {
      case 'ink': {
        // Get the most favorited token or most recent if no favorites
        const { data, error } = await supabase
          .from(TABLES.TOKENS)
          .select('*')
          .eq('network', 'ink')
          .order('favorites_count', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (error || !data) return null;
        return { ...data, network: data.network || 'ink', chain: 'ink' as ChainFilter };
      }

      case 'eth': {
        // For ETH, you might want to use a specific featured token address
        // For now, get the most recent
        const tokens = await getETHTokens({ limit: 1, orderBy: 'created_at', ascending: false });
        if (tokens.length === 0) return null;
        return { ...tokens[0], network: tokens[0].network || 'ethereum', chain: 'eth' as ChainFilter };
      }

      case 'wlfi': {
        const tokens = await getUSD1Tokens({ limit: 1, orderBy: 'created_at', ascending: false });
        if (tokens.length === 0) return null;
        return { ...tokens[0], network: tokens[0].network || 'ethereum', chain: 'wlfi' as ChainFilter };
      }

      case 'mon': {
        const { data, error } = await supabase
          .from('tokens_monad')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (error || !data) return null;
        return { ...data, network: data.network || 'monad', chain: 'mon' as ChainFilter };
      }

      case 'sol': {
        const tokens = await getSolanaTokens({ limit: 1, orderBy: 'created_at', ascending: false });
        if (tokens.length === 0) return null;
        return { 
          ...tokens[0], 
          contract_address: tokens[0].mint_address,
          network: 'solana', 
          chain: 'sol' as ChainFilter 
        };
      }

      default:
        return null;
    }
  } catch (error) {
    console.error(`[UnifiedTokenFetcher] Error fetching featured token for ${chain}:`, error);
    return null;
  }
}

/**
 * Fetch top favorited tokens for a specific chain
 */
export async function fetchTopFavoritedTokensByChain(
  chain: ChainFilter,
  limit: number = 5
): Promise<UnifiedToken[]> {
  try {
    switch (chain) {
      case 'ink': {
        const { data, error } = await supabase
          .from(TABLES.TOKENS)
          .select('*')
          .eq('network', 'ink')
          .order('favorites_count', { ascending: false })
          .limit(limit);

        if (error) throw error;
        return (data || []).map((token: TokenData) => ({
          ...token,
          chain: 'ink' as ChainFilter,
        }));
      }

      case 'eth': {
        // ETH table might not have favorites_count, so get most recent
        const tokens = await getETHTokens({ limit, orderBy: 'created_at', ascending: false });
        return tokens.map((token: ETHTokenData) => ({
          ...token,
          network: token.network || 'ethereum',
          chain: 'eth' as ChainFilter,
        }));
      }

      case 'wlfi': {
        const tokens = await getUSD1Tokens({ limit, orderBy: 'created_at', ascending: false });
        return tokens.map((token: USD1TokenData) => ({
          ...token,
          network: token.network || 'ethereum',
          chain: 'wlfi' as ChainFilter,
        }));
      }

      case 'mon': {
        const { data, error } = await supabase
          .from('tokens_monad')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(limit);

        if (error) throw error;
        return (data || []).map((token: any) => ({
          ...token,
          network: token.network || 'monad',
          chain: 'mon' as ChainFilter,
        }));
      }

      case 'sol': {
        const tokens = await getSolanaTokens({ limit, orderBy: 'created_at', ascending: false });
        return tokens.map((token: SolanaTokenData) => ({
          ...token,
          contract_address: token.mint_address,
          network: 'solana',
          chain: 'sol' as ChainFilter,
        }));
      }

      default:
        return [];
    }
  } catch (error) {
    console.error(`[UnifiedTokenFetcher] Error fetching top favorited tokens for ${chain}:`, error);
    return [];
  }
}

